import { useEffect, useMemo, useState } from 'react';
import type { Transaction } from '@balance/domain';
import { api, type AllocationPayload, type LeisureInfo } from '../api.js';
import { centsToInput, eur, inputToCents, periodLabel, shortDateLabel } from '../format.js';
import { useApp } from '../state.js';
import { ConceptWizard } from './ConceptWizard.js';
import { Modal } from './Modal.js';
import { Badge } from './ui.js';

interface Part {
  id?: string;
  conceptId: string;
  period: string;
  amount: string;
  subPeriod: string;
  occurrenceKeys: string[];
  recoverable: boolean;
  recoverableAmount: string;
  dueOn: string;
  isMainMonthly: boolean;
  note: string;
}

interface Props {
  transactionId: string | null;
  defaults?: Partial<{ description: string; amountCents: number; date: string; time: string | null; direction: 'out' | 'in'; source: Transaction['source'] }>;
  onClose: () => void;
}

const SOURCES: Array<[Transaction['source'], string]> = [
  ['cash', 'Efectivo'],
  ['card', 'Tarjeta'],
  ['transfer', 'Transferencia'],
  ['bizum', 'Bizum'],
  ['remitly', 'Remitly'],
  ['manual', 'Pago manual'],
  ['email', 'Correo / extracto'],
  ['other', 'Otro'],
];

/** Detalle y edición de un movimiento: importe total, reparto en partes, períodos, recuperables (DISENO.md). */
export function TransactionEditor({ transactionId, defaults, onClose }: Props) {
  const { catalog, balance, movements, run, period: currentPeriod } = useApp();
  const existing = movements?.transactions.find((t) => t.id === transactionId) ?? null;
  const existingAllocs = useMemo(() => (movements?.allocations ?? []).filter((a) => a.transactionId === transactionId), [movements, transactionId]);
  const today = balance?.today ?? new Date().toISOString().slice(0, 10);
  const defaultPeriod = currentPeriod || balance?.currentPeriod || today.slice(0, 7);
  const [tx, setTx] = useState({
    date: existing?.date ?? defaults?.date ?? today,
    time: existing?.time ?? defaults?.time ?? '',
    description: existing?.description ?? defaults?.description ?? '',
    amount: centsToInput(existing?.amountCents ?? defaults?.amountCents ?? null),
    direction: existing?.direction ?? defaults?.direction ?? 'out',
    source: existing?.source ?? defaults?.source ?? 'cash',
    personId: existing?.personId ?? '',
    isInternalTransfer: existing?.isInternalTransfer ?? false,
    notes: existing?.notes ?? '',
  });
  const receivablesByAlloc = useMemo(() => new Map((movements?.receivables ?? []).map((r) => [r.allocationId, r])), [movements]);
  const [parts, setParts] = useState<Part[]>(() =>
    existingAllocs.length
      ? existingAllocs.map((a) => ({
          id: a.id,
          conceptId: a.conceptId,
          period: a.period,
          amount: centsToInput(a.amountCents),
          subPeriod: a.subPeriod ?? '',
          occurrenceKeys: a.occurrenceKeys,
          recoverable: a.recoverableCents > 0,
          recoverableAmount: centsToInput(a.recoverableCents || null),
          dueOn: receivablesByAlloc.get(a.id)?.dueOn ?? '',
          isMainMonthly: a.isMainMonthly,
          note: a.note ?? '',
        }))
      : [],
  );
  const [error, setError] = useState<string | null>(null);
  const [leisureByPeriod, setLeisureByPeriod] = useState<Record<string, LeisureInfo>>({});
  const [wizardFor, setWizardFor] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  const concepts = useMemo(() => (catalog?.concepts ?? []).filter((c) => (tx.direction === 'in' ? c.direction === 'income' : c.direction === 'expense')), [catalog, tx.direction]);
  const conceptById = useMemo(() => new Map((catalog?.concepts ?? []).map((c) => [c.id, c])), [catalog]);
  const versionsOf = (conceptId: string) => (catalog?.versions ?? []).filter((v) => v.conceptId === conceptId);
  const isWeekendBudget = (conceptId: string) => versionsOf(conceptId).some((v) => v.frequency === 'weekend');
  const periods = balance?.periods ?? [];

  useEffect(() => {
    for (const p of parts) {
      if (p.conceptId && isWeekendBudget(p.conceptId) && p.period && !leisureByPeriod[p.period]) {
        api.leisure(p.period).then((l) => setLeisureByPeriod((m) => ({ ...m, [p.period]: l }))).catch(() => undefined);
      }
    }
  }, [parts]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = inputToCents(tx.amount) ?? 0;
  const sum = parts.reduce((a, p) => a + (inputToCents(p.amount) ?? 0), 0);
  const owed = parts.reduce((a, p) => a + (p.recoverable ? inputToCents(p.recoverableAmount) ?? 0 : 0), 0);

  const addPart = (preset: Partial<Part> = {}) =>
    setParts((ps) => [...ps, { conceptId: '', period: defaultPeriod, amount: ps.length === 0 ? tx.amount : centsToInput(Math.max(0, total - sum) || null), subPeriod: '', occurrenceKeys: [], recoverable: false, recoverableAmount: '', dueOn: '', isMainMonthly: false, note: '', ...preset }]);
  const setPart = (i: number, patch: Partial<Part>) => setParts((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));

  function occurrencesFor(conceptId: string, period: string) {
    const row = balance?.rows.find((r) => r.conceptId === conceptId);
    return row?.cells[period]?.occurrences ?? [];
  }

  function validate(): string | null {
    if (!tx.description.trim()) return 'Indicá una descripción.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(tx.date)) return 'Fecha inválida.';
    if (tx.time && !/^\d{2}:\d{2}$/.test(tx.time)) return 'Hora inválida (HH:MM).';
    if (total <= 0) return 'El importe debe ser mayor que cero.';
    if (tx.isInternalTransfer && parts.length) return 'Una transferencia entre cuentas propias no se imputa a conceptos.';
    for (const [i, p] of parts.entries()) {
      const a = inputToCents(p.amount);
      if (!p.conceptId) return `Parte ${i + 1}: elegí un concepto.`;
      if (a === null || a <= 0) return `Parte ${i + 1}: importe inválido.`;
      if (!/^\d{4}-\d{2}$/.test(p.period)) return `Parte ${i + 1}: elegí el mes.`;
      if (p.recoverable) {
        const r = inputToCents(p.recoverableAmount);
        if (r === null || r <= 0 || r > a) return `Parte ${i + 1}: la devolución debe ser positiva y no superar la parte.`;
      }
    }
    if (sum > total) return `Las partes suman ${eur(sum)} y superan el movimiento (${eur(total)}).`;
    return null;
  }

  async function save() {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSaving(true);
    const allocations: AllocationPayload[] = parts.map((p) => ({
      id: p.id,
      conceptId: p.conceptId,
      period: p.period,
      occurrenceKeys: p.occurrenceKeys,
      subPeriod: p.subPeriod || null,
      amountCents: inputToCents(p.amount) ?? 0,
      recoverableCents: p.recoverable ? inputToCents(p.recoverableAmount) ?? 0 : 0,
      isMainMonthly: p.isMainMonthly,
      note: p.note || null,
      dueOn: p.recoverable && p.dueOn ? p.dueOn : null,
    }));
    const payload = { date: tx.date, time: tx.time || null, description: tx.description.trim(), amountCents: total, direction: tx.direction, source: tx.source, personId: tx.personId || null, isInternalTransfer: tx.isInternalTransfer, notes: tx.notes || null };
    const ok = await run(async () => {
      if (existing) {
        await api.updateTransaction(existing.id, payload);
        await api.replaceAllocations(existing.id, allocations);
      } else {
        await api.createTransaction({ transaction: payload, allocations });
      }
      return true;
    }, `Movimiento guardado con ${allocations.length} imputación${allocations.length === 1 ? '' : 'es'}${sum < total && !tx.isInternalTransfer ? ` · ${eur(total - sum)} sin repartir` : ''}`);
    setSaving(false);
    if (ok) onClose();
  }

  async function remove() {
    if (!existing) return;
    const ok = await run(() => api.deleteTransaction(existing.id), 'Movimiento eliminado');
    if (ok !== undefined) onClose();
  }

  const remaining = total - sum;

  return (
    <>
      <Modal
        title={existing ? 'Detalle del movimiento' : tx.direction === 'in' ? 'Registrar una entrada' : 'Cargar un gasto'}
        subtitle={existing ? `Creado ${existing.createdAt.slice(0, 10)} · origen ${SOURCES.find((s) => s[0] === existing.source)?.[1] ?? existing.source}` : 'Efectivo o pagos que no llegan por aviso. Si luego llega el mismo movimiento, se concilia sin duplicar.'}
        onClose={onClose}
        side
        error={error}
        actions={
          <>
            {existing ? (
              <button type="button" className="button danger left" onClick={() => setConfirmDelete(true)}>
                Eliminar
              </button>
            ) : null}
            <button type="button" className="button primary" disabled={saving} onClick={() => void save()}>
              Guardar
            </button>
          </>
        }
      >
        <div className="field-row">
          <label>
            Descripción
            <input data-autofocus value={tx.description} onChange={(e) => setTx({ ...tx, description: e.target.value })} placeholder="Mercadona, Bizum, Remitly…" />
          </label>
          <label>
            Importe total (€)
            <input inputMode="decimal" value={tx.amount} onChange={(e) => setTx({ ...tx, amount: e.target.value })} placeholder="0,00" />
          </label>
          <label>
            Fecha de la operación
            <input type="date" value={tx.date} onChange={(e) => setTx({ ...tx, date: e.target.value })} />
          </label>
          <label>
            Hora (si se conoce)
            <input type="time" value={tx.time} onChange={(e) => setTx({ ...tx, time: e.target.value })} />
          </label>
          <label>
            Sentido
            <select value={tx.direction} onChange={(e) => setTx({ ...tx, direction: e.target.value as 'out' | 'in' })} disabled={parts.length > 0}>
              <option value="out">Salida (gasto)</option>
              <option value="in">Entrada (ingreso o devolución)</option>
            </select>
          </label>
          <label>
            Origen
            <select value={tx.source} onChange={(e) => setTx({ ...tx, source: e.target.value as Transaction['source'] })}>
              {SOURCES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label>
            Quién pagó (informativo)
            <select value={tx.personId} onChange={(e) => setTx({ ...tx, personId: e.target.value })}>
              <option value="">Hogar</option>
              {catalog?.persons.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="check" style={{ alignSelf: 'end' }}>
            <input type="checkbox" checked={tx.isInternalTransfer} onChange={(e) => setTx({ ...tx, isInternalTransfer: e.target.checked })} />
            Transferencia entre cuentas propias (no es gasto ni ingreso)
          </label>
        </div>

        {!tx.isInternalTransfer ? (
          <>
            {parts.map((p, i) => {
              const concept = conceptById.get(p.conceptId);
              const weekend = p.conceptId ? isWeekendBudget(p.conceptId) : false;
              const occs = p.conceptId && concept?.behavior !== 'budget' ? occurrencesFor(p.conceptId, p.period) : [];
              const leisure = leisureByPeriod[p.period];
              return (
                <div className="split" key={i}>
                  <div className="panelhead" style={{ marginBottom: 8 }}>
                    <h3>Parte {i + 1}</h3>
                    <button type="button" className="button small" onClick={() => setParts((ps) => ps.filter((_, j) => j !== i))}>
                      Quitar
                    </button>
                  </div>
                  <div className="field-row three">
                    <label>
                      Importe (€)
                      <input inputMode="decimal" value={p.amount} onChange={(e) => setPart(i, { amount: e.target.value })} />
                    </label>
                    <label>
                      Concepto
                      <select value={p.conceptId} onChange={(e) => setPart(i, { conceptId: e.target.value, occurrenceKeys: [], subPeriod: '', isMainMonthly: false })}>
                        <option value="">Elegir…</option>
                        {(catalog?.categories ?? []).map((cat) => {
                          const list = concepts.filter((c) => c.categoryId === cat.id);
                          return list.length ? (
                            <optgroup key={cat.id} label={cat.name}>
                              {list.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </optgroup>
                          ) : null;
                        })}
                        {concepts.filter((c) => !c.categoryId).length ? (
                          <optgroup label="Sin categoría">
                            {concepts
                              .filter((c) => !c.categoryId)
                              .map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                          </optgroup>
                        ) : null}
                      </select>
                    </label>
                    <label>
                      Mes
                      <select value={p.period} onChange={(e) => setPart(i, { period: e.target.value, occurrenceKeys: [], subPeriod: '' })}>
                        {periods.map((per) => (
                          <option key={per.period} value={per.period}>
                            {per.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  {weekend ? (
                    <label>
                      Fin de semana
                      <select value={p.subPeriod} onChange={(e) => setPart(i, { subPeriod: e.target.value })}>
                        <option value="">Sin asignar (pendiente de elegir)</option>
                        {leisure?.weekends.map((w) => (
                          <option key={w.key} value={w.key}>
                            {w.label}
                            {w.crossesMonth ? ' · cruza de mes' : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  {occs.length ? (
                    <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
                      <legend className="muted" style={{ fontSize: 12 }}>
                        Vencimientos que cubre este pago (ninguno marcado = cubre el mes completo)
                      </legend>
                      {occs.map((o) => (
                        <label className="check" key={o.key} style={{ padding: '3px 0' }}>
                          <input
                            type="checkbox"
                            checked={p.occurrenceKeys.includes(o.key)}
                            onChange={(e) => setPart(i, { occurrenceKeys: e.target.checked ? [...p.occurrenceKeys, o.key] : p.occurrenceKeys.filter((k) => k !== o.key) })}
                          />
                          {shortDateLabel(o.date)} · {eur(o.amountCents)}
                          {o.covered && !p.occurrenceKeys.includes(o.key) ? <Badge kind="neutral">ya cubierto</Badge> : null}
                        </label>
                      ))}
                    </fieldset>
                  ) : null}
                  {concept?.behavior === 'budget' && !weekend ? (
                    <label className="check">
                      <input type="checkbox" checked={p.isMainMonthly} onChange={(e) => setPart(i, { isMainMonthly: e.target.checked })} />
                      Compra mensual principal de {periodLabel(p.period)}
                    </label>
                  ) : null}
                  {tx.direction === 'out' ? (
                    <label className="check">
                      <input type="checkbox" checked={p.recoverable} onChange={(e) => setPart(i, { recoverable: e.target.checked, dueOn: e.target.checked && !p.dueOn ? addDays(tx.date, catalog?.household.settings.receivableDueDays ?? 10) : p.dueOn })} />
                      Me tienen que devolver una parte
                    </label>
                  ) : null}
                  {p.recoverable ? (
                    <div className="field-row">
                      <label>
                        Importe por recuperar (€)
                        <input inputMode="decimal" value={p.recoverableAmount} onChange={(e) => setPart(i, { recoverableAmount: e.target.value })} />
                      </label>
                      <label>
                        Vence el
                        <input type="date" value={p.dueOn} onChange={(e) => setPart(i, { dueOn: e.target.value })} />
                      </label>
                    </div>
                  ) : null}
                  <label>
                    Nota (opcional)
                    <input value={p.note} onChange={(e) => setPart(i, { note: e.target.value })} placeholder="Por ejemplo: tres sesiones de octubre" />
                  </label>
                </div>
              );
            })}
            <div className="inline" style={{ marginTop: 10 }}>
              <button type="button" className="button" onClick={() => addPart()}>
                {parts.length ? '+ Dividir entre otro destino' : '+ Imputar a un concepto'}
              </button>
              <button type="button" className="button" onClick={() => { addPart(); setWizardFor(parts.length); }}>
                + Crear gasto puntual y asignar
              </button>
            </div>
            <div className="breakdown" style={{ marginTop: 16 }} aria-live="polite">
              Imputado {eur(sum)} · sin repartir <strong className={remaining > 0 ? 'negative' : ''}>{eur(Math.max(0, remaining))}</strong> · parte propia {eur(sum - owed)} · por recuperar {eur(owed)}
              {remaining > 0 && parts.length ? <div className="muted">Lo no repartido queda visible como pendiente y afecta al balance provisional.</div> : null}
            </div>
          </>
        ) : (
          <div className="hint">Las transferencias entre cuentas propias no generan ingreso ni gasto familiar; sólo cambian la liquidez de cada cuenta.</div>
        )}
        <label style={{ marginTop: 16 }}>
          Notas
          <textarea rows={2} value={tx.notes} onChange={(e) => setTx({ ...tx, notes: e.target.value })} />
        </label>
        {existing ? (
          <p className="muted">
            Fecha original {existing.date}
            {existing.time ? ` ${existing.time}` : ''} · importe {eur(existing.amountCents)} · origen {existing.source}
            {existing.externalRef ? ` · ref. ${existing.externalRef}` : ''}
          </p>
        ) : null}
      </Modal>
      {wizardFor !== null ? (
        <ConceptWizard
          mode="create"
          presetBehavior="one_off"
          presetName={tx.description}
          presetAmountCents={inputToCents(parts[wizardFor]?.amount ?? '') ?? (total - sum > 0 ? total - sum : total)}
          presetDate={tx.date}
          onClose={() => setWizardFor(null)}
          onSaved={(id) => {
            setPart(wizardFor, { conceptId: id });
            setWizardFor(null);
          }}
        />
      ) : null}
      {confirmDelete ? (
        <Modal
          title="Eliminar movimiento"
          onClose={() => setConfirmDelete(false)}
          actions={
            <button type="button" className="button danger" onClick={() => void remove()}>
              Eliminar definitivamente
            </button>
          }
        >
          <p>Se eliminan el movimiento, sus imputaciones y sus cuentas por cobrar. Los totales se recalculan. Esta acción queda en el historial.</p>
        </Modal>
      ) : null}
    </>
  );
}

function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
